import * as Switch from '@radix-ui/react-switch';
import type { VariantProps } from '#pie/utils/TailwindUtils';
import { tv } from '#pie/utils/TailwindUtils';
import { Text } from '../text/Text';

export interface InputToggleProps
  extends Omit<Switch.SwitchProps, 'onChange' | 'value'>,
    Omit<VariantProps<typeof inputToggle>, 'checked'> {
  label?: string;
  onChange?: (value?: boolean) => void;
  ref?: React.Ref<HTMLButtonElement>;
}

export const InputToggle = function InputToggle({
  ref,
  label,
  isError,
  onCheckedChange,
  onChange,
  ...props
}: InputToggleProps) {
  const { disabled, id, checked } = props;
  const styles = inputToggle({ checked: !!checked, disabled, isError });

  return (
    <label className={styles.wrapper()} htmlFor={id}>
      <Switch.Root
        onCheckedChange={(checked) => onChange?.(checked)}
        aria-label={label}
        className={styles.root()}
        {...props}
      >
        <Switch.Thumb className={styles.thumb()} />
      </Switch.Root>

      {!!label && <Text className={styles.label()}>{label}</Text>}
    </label>
  );
};

const inputToggle = tv({
  slots: {
    label: 'text-content-body cursor-pointer',
    root: 'relative h-7 w-12 rounded-full bg-surface-subtle-gray p-1 ',
    thumb:
      'block h-5 w-5 translate-x-0 rounded-full bg-white transition-transform duration-300 data-[state=checked]:translate-x-full',
    wrapper: 'flex items-center gap-3',
  },
  variants: {
    checked: {
      true: {
        root: 'bg-action-primary-enabled focus-visible:outline-action-primary-enabled shadow-none',
      },
    },
    disabled: {
      true: {
        label: 'text-content-disabled cursor-not-allowed',
        root: 'cursor-not-allowed opacity-50',
        wrapper: 'cursor-not-allowed',
      },
    },
    isError: {
      true: {
        label: 'text-content-error',
        root: 'data-[state=checked]:bg-support-error-bold data-[state=checked]:outline-support-error-bold bg-surface-error outline-support-error-bold',
      },
    },
  },
});
